import type { ReactiveControllerHost } from 'lit'

import { wrap } from '../number.js'

export interface ActiveDescendantOptions {
  /** The number of navigable rows. */
  count: () => number
  /** Whether the row at `index` can be activated (skip disabled rows). */
  isEnabled: (index: number) => boolean
  /** Wrap at the ends. Defaults to `true`. */
  loop?: () => boolean
  /** The DOM id of the row at `index`, used for `aria-activedescendant`. */
  optionId?: (index: number) => string | undefined
}

/**
 * Tracks the active row of a virtual-focus listbox (combobox / filter dropdown):
 * the focus stays on the input/trigger while an `activeIndex` roams the rows and
 * is surfaced as `aria-activedescendant`. Centralises the `nextEnabledIndex` /
 * `move` / wrap math the combobox and filter-dropdown previously duplicated.
 */
export class ActiveDescendantController {
  private _activeIndex = -1

  // A host-bound helper rather than a lifecycle ReactiveController: it owns no
  // connect/update hooks, it only reads/writes the active index and asks the
  // host to re-render, so it doesn't register via `addController`.
  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private options: ActiveDescendantOptions,
  ) {}

  /** The active row index, or `-1` when nothing is active. */
  get activeIndex(): number {
    return this._activeIndex
  }

  /** The active row's id for `aria-activedescendant`, or `undefined`. */
  get activeDescendant(): string | undefined {
    return this._activeIndex >= 0 ? this.options.optionId?.(this._activeIndex) : undefined
  }

  /** Set the active index, requesting a host update when it changes. */
  setActiveIndex(index: number) {
    if (index !== this._activeIndex) {
      this._activeIndex = index
      this.host.requestUpdate()
    }
  }

  /** Clear the active row. */
  reset() {
    this.setActiveIndex(-1)
  }

  /**
   * The next enabled row from `from` stepping by `dir`, wrapping (when looping)
   * and skipping disabled rows. Returns `-1` if no row is enabled.
   */
  nextEnabledIndex(from: number, dir: 1 | -1): number {
    const count = this.options.count()
    if (count === 0) {
      return -1
    }
    const loop = this.options.loop?.() ?? true

    let index = from
    if (index < 0 || index >= count) {
      if (!loop) {
        return -1
      }
      index = wrap(index, 0, count - 1)
    }

    for (let i = 0; i < count; i++) {
      if (this.options.isEnabled(index)) {
        return index
      }
      const stepped = index + dir
      if (stepped < 0 || stepped >= count) {
        if (!loop) {
          return -1
        }
        index = wrap(stepped, 0, count - 1)
      }
      else {
        index = stepped
      }
    }
    return -1
  }

  /**
   * Move the active row by `delta` (its sign is the direction). Starts from the
   * matching end when nothing is active. Returns `true` if it moved.
   */
  move(delta: number): boolean {
    const count = this.options.count()
    if (count === 0) {
      return false
    }
    const dir = delta > 0 ? 1 : -1
    const next = this._activeIndex < 0
      ? this.nextEnabledIndex(delta > 0 ? 0 : count - 1, dir)
      : this.nextEnabledIndex(this._activeIndex + delta, dir)

    if (next === -1) {
      return false
    }
    this.setActiveIndex(next)
    return true
  }

  /** Activate the first enabled row (Home). Returns `true` if one was found. */
  first(): boolean {
    const next = this.nextEnabledIndex(0, 1)
    if (next === -1) {
      return false
    }
    this.setActiveIndex(next)
    return true
  }

  /** Activate the last enabled row (End). Returns `true` if one was found. */
  last(): boolean {
    const next = this.nextEnabledIndex(this.options.count() - 1, -1)
    if (next === -1) {
      return false
    }
    this.setActiveIndex(next)
    return true
  }
}
